/**
 * Converts a UTC time string + date to the given IANA timezone.
 * @param utcTime  "HH:MM:SS" or "HH:MM" in UTC
 * @param date     "YYYY-MM-DD" — the trade date (needed for correct DST offset)
 * @param tz       IANA timezone string e.g. "America/New_York"
 * @returns        "HH:MM:SS" in the target timezone
 */
export function utcTimeToTz(utcTime: string, date: string, tz: string): string {
  const padded = utcTime.length === 5 ? utcTime + ':00' : utcTime;
  const dt = new Date(`${date}T${padded}Z`);
  const result = dt.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  // toLocaleTimeString can return "24:00:00" for midnight — normalize to "00:00:00"
  return result.replace(/^24:/, '00:');
}

/**
 * Converts a local time string (in the given timezone) + date back to UTC.
 * Uses 3-iteration convergence to handle DST boundaries correctly.
 * @param localTime  "HH:MM" or "HH:MM:SS" in the target timezone
 * @param date       "YYYY-MM-DD" — the trade date
 * @param tz         IANA timezone string
 * @returns          "HH:MM:SS" in UTC
 */
export function tzTimeToUtc(localTime: string, date: string, tz: string): string {
  const paddedTime = localTime.length === 5 ? localTime + ':00' : localTime;
  const [h, m, s] = paddedTime.split(':').map(Number);
  let guess = new Date(`${date}T${paddedTime}Z`);

  // 3 iterations handle standard DST transitions (1-hour shift).
  // Ambiguous DST hours (e.g. 1:30 AM on fall-back night) may not converge,
  // but trading hours (09:30–16:00 ET) never fall in that range.
  for (let i = 0; i < 3; i++) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(guess);
    const tzH = +(parts.find((p) => p.type === 'hour')?.value ?? '0');
    const tzM = +(parts.find((p) => p.type === 'minute')?.value ?? '0');
    const tzS = +(parts.find((p) => p.type === 'second')?.value ?? '0');
    const diffMs = ((h - tzH) * 3600 + (m - tzM) * 60 + (s - tzS)) * 1000;
    if (diffMs === 0) break;
    guess = new Date(guess.getTime() + diffMs);
  }

  return guess.toISOString().slice(11, 19);
}

/**
 * Null-safe wrapper for display — returns '' when utcTime is null/undefined.
 */
export function formatTimeTz(utcTime: string | null | undefined, date: string, tz: string): string {
  if (!utcTime) return '';
  return utcTimeToTz(utcTime, date, tz);
}

/**
 * Converts a UTC hour-of-day (0-23) to the equivalent hour in the given timezone.
 * Uses today's date for the offset unless a date is given — DST shifts the
 * mapping by an hour across transitions, acceptable for hour-bucket display.
 */
export function utcHourToTz(hourUtc: number, tz: string, date?: string): number {
  const d = date ?? new Date().toISOString().slice(0, 10);
  const local = utcTimeToTz(`${String(hourUtc).padStart(2, '0')}:00:00`, d, tz);
  return parseInt(local.slice(0, 2), 10);
}

/**
 * Returns the short timezone abbreviation for the given IANA tz + date.
 * e.g. "EST", "EDT", "CET", "UTC"
 */
export function tzAbbr(tz: string, date: string): string {
  if (!date) return '';
  // Noon UTC avoids day-boundary ambiguity for all timezones (UTC-11 to UTC+14).
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  }).formatToParts(new Date(`${date}T12:00:00Z`));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
}
